import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import { TeamCostDisplay } from './TeamCostDisplay';

interface AverageTeamCostProps {
  teamId: string;
}

export const AverageTeamCost = ({
  teamId
}: AverageTeamCostProps) => {
  const { t } = useLanguage();
  const { isAdmin } = useUserRoles();
  
  const { data: costs, isLoading } = useQuery({
    queryKey: ['team-average-costs', teamId],
    queryFn: async () => {
      // Get the team to find out which admin created it
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('created_by')
        .eq('id', teamId)
        .single();
        
      if (teamError) throw teamError;
      if (!team?.created_by) throw new Error('Team has no creator admin');

      const adminId = team.created_by;

      // First get team members
      const { data: members, error: membersError } = await supabase
        .from('team_members')
        .select('profile_id')
        .eq('team_id', teamId);
        
      if (membersError) throw membersError;
      if (!members || members.length === 0) return {
        weighted: null,
        absolute: null,
        absoluteEuros: null
      };

      // Get trietia for each member
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('trietia')
        .in('id', members.map(m => m.profile_id));
        
      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) return {
        weighted: null,
        absolute: null,
        absoluteEuros: null
      };

      // Get hourly costs for this admin's configuration
      const { data: hourlyCosts, error: costsError } = await supabase
        .from('hourly_costs')
        .select('cost_per_hour, trietia')
        .eq('admin_id', adminId);
        
      if (costsError) throw costsError;
      if (!hourlyCosts || hourlyCosts.length === 0) return {
        weighted: null,
        absolute: null,
        absoluteEuros: null
      };
      
      let totalWeightedCost = 0;
      let totalAbsoluteCost = 0;
      let validMembersCount = 0;
      let totalTrietia = 0;
      
      profiles.forEach(profile => {
        const trietia = parseInt(String(profile.trietia || 0), 10);
        const costEntry = hourlyCosts.find(c => c.trietia === trietia);
        
        if (costEntry) {
          totalWeightedCost += parseFloat(String(costEntry.cost_per_hour));
          totalTrietia += trietia;
          validMembersCount++;

          // Calculate absolute cost based only on trietia percentage
          // Each trietia represents a 10% increase from base salary
          const baseHourlyCost = hourlyCosts.find(c => c.trietia === 0)?.cost_per_hour || 0;
          const absoluteCost = parseFloat(String(baseHourlyCost)) * (1 + trietia * 0.10);
          totalAbsoluteCost += absoluteCost;
        }
      });

      // Calculate all averages
      const weightedAverage = validMembersCount > 0 ? totalWeightedCost / validMembersCount : null;
      const absoluteAverage = validMembersCount > 0 ? totalTrietia / validMembersCount : null;
      const absoluteEurosAverage = validMembersCount > 0 ? totalAbsoluteCost / validMembersCount : null;
      
      return {
        weighted: weightedAverage,
        absolute: absoluteAverage,
        absoluteEuros: absoluteEurosAverage
      };
    },
    enabled: isAdmin
  });
  
  if (!isAdmin || isLoading || !costs || !costs.weighted) return null;
  
  const averageHourly = costs.weighted;
  
  return (
    <TeamCostDisplay
      cost={averageHourly}
      tooltipText="Team Hourly Cost"
      displayLabel="Median €/h"
      borderClass="border-blue-500"
      textClass="text-blue-600"
    />
  );
};
